// Dienstleister-Widgets, Panels und Seitenleiste (Handoff Dienstleister §9, Prototyp W/PT/SN).

import {
  AddressBook, Bank, Buildings, CalendarBlank, ClockCounterClockwise, Coins, DeviceMobile, FolderSimple, Funnel, Medal, NotePencil, Receipt,
  SealCheck, Signature, Sparkle, SquaresFour, Star, Tag, Ticket, UsersFour, type Icon,
} from '@phosphor-icons/react';
import type { WidgetGroesse } from './ui/Widget';

export type DlPanel =
  | 'profil' | 'tickets' | 'rechnungen' | 'objekte' | 'kategorien' | 'konditionen' | 'vertraege' | 'nachweise' | 'bewertung' | 'regeln'
  | 'bank' | 'kontakt' | 'ansprech' | 'portal' | 'termine' | 'aktivitaet' | 'dokumente' | 'notizen' | 'ki';
export type DlGruppe = 'alle' | 'leist' | 'auft' | 'vert' | 'komm';
export interface DlWidgetDef { key: DlPanel; gruppe: Exclude<DlGruppe, 'alle'>; groesse: WidgetGroesse }

export const DL_GRUPPEN: readonly { key: DlGruppe; label: string }[] = [
  { key: 'alle', label: 'Alle' }, { key: 'leist', label: 'Leistung' }, { key: 'auft', label: 'Aufträge & Finanzen' },
  { key: 'vert', label: 'Verträge & Nachweise' }, { key: 'komm', label: 'Kommunikation' },
];
export const istDlGruppe = (v: string | null): v is DlGruppe => DL_GRUPPEN.some(g => g.key === v);

/** Reihenfolge und Größen wie im Prototyp (Array W). */
const ALLE_WIDGETS: readonly DlWidgetDef[] = [
  { key: 'profil', gruppe: 'leist', groesse: 'gross' },
  { key: 'tickets', gruppe: 'auft', groesse: 'breit' },
  { key: 'rechnungen', gruppe: 'auft', groesse: 'normal' },
  { key: 'objekte', gruppe: 'leist', groesse: 'normal' },
  { key: 'kategorien', gruppe: 'leist', groesse: 'normal' },
  { key: 'konditionen', gruppe: 'auft', groesse: 'normal' },
  { key: 'vertraege', gruppe: 'vert', groesse: 'normal' },
  { key: 'nachweise', gruppe: 'vert', groesse: 'normal' },
  { key: 'bewertung', gruppe: 'leist', groesse: 'normal' },
  { key: 'regeln', gruppe: 'leist', groesse: 'normal' },
  { key: 'bank', gruppe: 'auft', groesse: 'normal' },
  { key: 'kontakt', gruppe: 'komm', groesse: 'breit' },
  { key: 'ansprech', gruppe: 'komm', groesse: 'normal' },
  { key: 'portal', gruppe: 'komm', groesse: 'normal' },
  { key: 'termine', gruppe: 'komm', groesse: 'normal' },
  { key: 'aktivitaet', gruppe: 'komm', groesse: 'breit' },
  { key: 'dokumente', gruppe: 'vert', groesse: 'normal' },
  { key: 'notizen', gruppe: 'komm', groesse: 'normal' },
  { key: 'ki', gruppe: 'komm', groesse: 'breit' },
];

export function dlWidgets(gruppe: DlGruppe): DlWidgetDef[] {
  return ALLE_WIDGETS.filter(d => gruppe === 'alle' || d.gruppe === gruppe);
}

/** Widget-Titel und Panel-Kopf (Prototyp PT). */
export const DL_PANEL_KOPF: Record<DlPanel, { widget: string; titel: string; icon: Icon }> = {
  profil: { widget: 'Leistung', titel: 'Leistung & Kennzahlen', icon: Medal },
  tickets: { widget: 'Aufträge & Tickets', titel: 'Aufträge & Tickets', icon: Ticket },
  rechnungen: { widget: 'Rechnungen', titel: 'Rechnungen', icon: Receipt },
  objekte: { widget: 'Objekte', titel: 'Objekt-Zuordnung', icon: Buildings },
  kategorien: { widget: 'Gewerke', titel: 'Gewerke & Kategorien', icon: Tag },
  konditionen: { widget: 'Konditionen', titel: 'Konditionen & Beauftragung', icon: Coins },
  vertraege: { widget: 'Verträge', titel: 'Verträge', icon: Signature },
  nachweise: { widget: 'Nachweise', titel: 'Nachweise & Versicherungen', icon: SealCheck },
  bewertung: { widget: 'Bewertung', titel: 'Bewertung', icon: Star },
  regeln: { widget: 'Ticket-Regeln', titel: 'Ticket-Regeln', icon: Funnel },
  bank: { widget: 'Bank & Steuer', titel: 'Bank & Steuer', icon: Bank },
  kontakt: { widget: 'Kontakt', titel: 'Stammdaten & Kontakt', icon: AddressBook },
  ansprech: { widget: 'Ansprechpartner', titel: 'Ansprechpartner', icon: UsersFour },
  portal: { widget: 'Dienstleisterportal', titel: 'Dienstleisterportal', icon: DeviceMobile },
  termine: { widget: 'Termine', titel: 'Termine', icon: CalendarBlank },
  aktivitaet: { widget: 'Kommunikation', titel: 'Kommunikation', icon: ClockCounterClockwise },
  dokumente: { widget: 'Dokumente', titel: 'Dokumente', icon: FolderSimple },
  notizen: { widget: 'Notizen', titel: 'Notizen', icon: NotePencil },
  ki: { widget: 'KI-Zusammenfassung', titel: 'KI-Zusammenfassung', icon: Sparkle },
};

export function istDlPanel(v: string | null): v is DlPanel {
  return v !== null && Object.prototype.hasOwnProperty.call(DL_PANEL_KOPF, v);
}

export type DlNavKey = DlPanel | 'uebersicht';
export interface DlNavAbschnitt { abschnitt: string; eintraege: { key: DlNavKey; label: string; icon: Icon }[] }

/** Seitenleiste im Dienstleistermodus (Prototyp SN); Leistung & KI nur über Widgets. */
export const DL_NAV: readonly DlNavAbschnitt[] = [
  { abschnitt: 'Dienstleister', eintraege: [
    { key: 'uebersicht', label: 'Übersicht', icon: SquaresFour }, { key: 'kontakt', label: 'Stammdaten', icon: AddressBook },
    { key: 'ansprech', label: 'Ansprechpartner', icon: UsersFour }, { key: 'portal', label: 'Dienstleisterportal', icon: DeviceMobile },
  ] },
  { abschnitt: 'Leistung', eintraege: [
    { key: 'kategorien', label: 'Gewerke', icon: Tag }, { key: 'objekte', label: 'Objekte', icon: Buildings },
    { key: 'regeln', label: 'Ticket-Regeln', icon: Funnel }, { key: 'bewertung', label: 'Bewertung', icon: Star },
  ] },
  { abschnitt: 'Aufträge & Finanzen', eintraege: [
    { key: 'tickets', label: 'Aufträge & Tickets', icon: Ticket }, { key: 'rechnungen', label: 'Rechnungen', icon: Receipt },
    { key: 'konditionen', label: 'Konditionen', icon: Coins }, { key: 'bank', label: 'Bank & Steuer', icon: Bank },
  ] },
  { abschnitt: 'Verträge & Nachweise', eintraege: [
    { key: 'vertraege', label: 'Verträge', icon: Signature }, { key: 'nachweise', label: 'Nachweise', icon: SealCheck },
    { key: 'dokumente', label: 'Dokumente', icon: FolderSimple },
  ] },
  { abschnitt: 'Kommunikation', eintraege: [
    { key: 'termine', label: 'Termine', icon: CalendarBlank }, { key: 'aktivitaet', label: 'Kommunikation', icon: ClockCounterClockwise },
    { key: 'notizen', label: 'Notizen', icon: NotePencil },
  ] },
];
